/**
 * Extract structured dice detail from a dice-roller-parser roll result.
 *
 * Recursively walks the RollBase tree and returns an array of "groups",
 * each representing one atomic die group (e.g. 2d6, 3d8, 4dF).
 *
 * Each group has:
 *   die    — face count (e.g. 6 for d6) or 'dF' for fate dice
 *   rolls  — array of individual die results
 *   value  — sum of valid rolls in this group
 *   op?    — operator from parent expression (+, -, *, /, etc.)
 *   fn?    — math function name (floor, ceil, round, abs)
 *
 * @param {object} rollResult — a node from the RollBase tree
 * @returns {Array<{die, rolls, value, op?, fn?}>}
 */
export function extractDiceDetail (rollResult) {
  // Atomic dice group (e.g. "2d6", "4dF") — result has .rolls with individual die results
  if (rollResult.type === 'die') {
    let dieLabel = rollResult.die?.value
    // Fate dice: die.type === 'fate', value is 0, but individual rolls are -1/0/+1
    if (rollResult.die?.type === 'fate') {
      dieLabel = 'dF'
    }
    return [{
      die: dieLabel,
      rolls: rollResult.rolls.map(r => ({
        value: r.roll ?? r.value,
        // "roll" type dice have .die (face count); "fateroll" type don't
        die: r.die,
        valid: r.valid,
        drop: r.drop,
        explode: r.explode,
        reroll: r.reroll,
        matched: r.matched,
        critical: r.critical,
        order: r.order,
        success: r.success
      })),
      value: rollResult.value
    }]
  }

  // Group roll {2d6, 3d8} — recurse into each inner expression
  if (rollResult.type === 'grouproll' && rollResult.dice) {
    return rollResult.dice.flatMap(child => extractDiceDetail(child))
  }

  // Dice expression roll (2d6+1d4 or 2d6+3) — recurse into each operand
  if (rollResult.type === 'diceexpressionroll' && rollResult.dice) {
    const groups = []
    for (let i = 0; i < rollResult.dice.length; i++) {
      const childGroups = extractDiceDetail(rollResult.dice[i])
      // Associate operator (+, -) with the group that follows it
      const op = i > 0 ? (rollResult.ops?.[i - 1] ?? '+') : undefined
      for (const g of childGroups) {
        if (op !== undefined) g.op = op
        groups.push(g)
      }
    }
    return groups
  }

  // Expression roll ((2d6+3)*4) — recurse into each operand
  if (rollResult.type === 'expressionroll' && rollResult.dice) {
    const groups = []
    for (let i = 0; i < rollResult.dice.length; i++) {
      const childGroups = extractDiceDetail(rollResult.dice[i])
      const op = i > 0 ? (rollResult.ops?.[i - 1] ?? '+') : undefined
      for (const g of childGroups) {
        if (op !== undefined) g.op = op
        groups.push(g)
      }
    }
    return groups
  }

  // Math function (floor(3d6/2)) — recurse into the inner expression
  if (rollResult.type === 'mathfunction' && rollResult.expr) {
    const groups = extractDiceDetail(rollResult.expr)
      // Filter out number groups — they're operands inside the function,
      // not standalone groups the UI should display as separate chips.
      .filter(g => !g.isNumber)
    // Annotate first group with the function name for display
    if (groups.length > 0) {
      groups[0].fn = rollResult.op
    }
    return groups
  }

  // Number value (e.g. the "3" in "2d6+3", or a standalone "5")
  if (rollResult.type === 'number') {
    return [{
      die: undefined,
      rolls: [],
      value: rollResult.value,
      isNumber: true
    }]
  }

  // Unknown / base types — no dice detail
  return []
}
